import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import { 
  Users, Plus, ArrowRight, Calendar, X, ChevronDown, ChevronUp, 
  DollarSign, CreditCard, ExternalLink, Trash2, UserPlus, Clock,
  ArrowLeftRight, CheckCircle, AlertCircle, CheckSquare, Square,
  ArrowUpDown, Filter, Lock, Loader2, MapPin, Navigation, Percent,
  RefreshCw, ShieldCheck
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import DistanceCalculator from './DistanceCalculator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styling for individual Stripe elements to match dark theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#22c55e',
      iconColor: '#22c55e',
    },
  },
};

// Stripe Submit Button Component for Queue using proper Elements API with separated fields
const QueueStripeSubmitButton = ({ onApprove, onError, disabled, termsAccepted, amount, customerEmail, metadata, postalCode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handleClick = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    
    if (!stripe || !elements) {
      onError(new Error("Payment system not ready. Please refresh and try again."));
      return;
    }
    
    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      onError(new Error("Card details not ready. Please try again."));
      return;
    }
    
    // Validate amount
    if (!amount || amount <= 0) {
      onError(new Error("Invalid payment amount. Please refresh and try again."));
      return;
    }
    
    // Round to 2 decimal places to avoid floating point issues
    const chargeAmount = Math.round(amount * 100) / 100;
    
    setPaying(true);
    try {
      console.log('💳 Queue Payment - Sending to Stripe:', {
        originalAmount: amount,
        chargeAmount: chargeAmount,
        chargeAmountFixed: chargeAmount.toFixed(2)
      });
      
      // Create PaymentIntent on backend with validated amount
      const intentResponse = await axios.post(`${API}/payments/create-intent`, {
        amount: chargeAmount,
        description: 'Pending Queue Payment',
        customer_email: customerEmail,
        metadata: metadata,
      });
      
      console.log('💳 PaymentIntent created:', intentResponse.data);

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.error || 'Failed to create payment');
      }

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Confirm the payment with Stripe using CardNumberElement
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            email: customerEmail || undefined,
            address: {
              postal_code: postalCode || undefined,
            },
          },
        },
      });

      if (error) {
        console.error("Payment error:", error);
        onError(new Error(error.message || "Payment failed"));
      } else if (paymentIntent.status === 'succeeded') {
        await onApprove({ 
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status 
        });
      }
    } catch (error) {
      console.error("Payment error details:", error);
      let errorMessage = "Payment failed";
      if (error.message) {
        errorMessage = error.message;
      }
      onError(new Error(errorMessage));
    } finally {
      setPaying(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || paying || !termsAccepted || !stripe}
      className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {paying ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Lock className="h-4 w-4" />
          Pay Now
        </>
      )}
    </Button>
  );
};

const PendingQueueSidebar = ({ 
  isOpen, 
  onToggle, 
  bookings = [], 
  classTypes = [],
  onRefreshBookings 
}) => {
  const [queueEntries, setQueueEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCreateClassDialog, setShowCreateClassDialog] = useState(false);
  const [showMoveToQueueDialog, setShowMoveToQueueDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedBookingForMove, setSelectedBookingForMove] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]); // Multi-select state
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'classType'
  const [filterClassType, setFilterClassType] = useState('all'); // Filter by class type
  const [editingNotesId, setEditingNotesId] = useState(null); // Track which entry is being edited
  const [editingNotesValue, setEditingNotesValue] = useState(''); // Current notes value being edited
  
  // Form state for manual add
  const [newEntry, setNewEntry] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    classTypeId: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    instructorNotes: '',
    originalClassDate: ''  // For reschedule tracking - date of original class
  });
  
  // Form state for create class
  const [createClassForm, setCreateClassForm] = useState({
    date: '',
    time: ''
  });
  
  // Form state for assign to class
  const [assignForm, setAssignForm] = useState({
    bookingId: '',
    asAddon: true
  });

  // Form for moving students to queue
  const [moveToQueueForm, setMoveToQueueForm] = useState({
    studentEmails: [], // empty = all
    moveAll: true
  });
  
  // Payment state for queue entries
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentEntry, setPaymentEntry] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [queuePostalCode, setQueuePostalCode] = useState('');
  const [queueCardFieldStatus, setQueueCardFieldStatus] = useState({
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  });
  const [manualFees, setManualFees] = useState({
    afterHours: false,
    rushFee: false,
    mileage: 0
  });
  // Fee rates from WooCommerce - no defaults
  const [feeRates, setFeeRates] = useState({
    afterHoursPerStudent: 0,
    rushPerStudent: 0,
    mileagePerMile: 0
  });
  const [targetBookingId, setTargetBookingId] = useState('');
  
  // Discount state - type can be 'dollar' or 'percent'
  const [discount, setDiscount] = useState({ type: 'dollar', value: 0, reason: '' });
  
  // Distance calculator state
  const [showDistanceCalc, setShowDistanceCalc] = useState(false);
  
  // Show older bookings toggle (for transfer to queue)
  const [showOlderBookings, setShowOlderBookings] = useState(false);
  
  // Class types with WooCommerce prices
  const [classTypesWithPrices, setClassTypesWithPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Fetch queue count immediately on mount (for the tab badge)
  useEffect(() => {
    fetchQueue();
  }, []);

  // Fetch heavier data only when sidebar is opened
  useEffect(() => {
    if (isOpen) {
      fetchQueue(); // Refresh when opened
      fetchPaymentConfig();
      fetchFeeRates();
      fetchClassTypePrices();
    }
  }, [isOpen]);
  
  // Fetch class type prices from WooCommerce
  const fetchClassTypePrices = async () => {
    setLoadingPrices(true);
    try {
      const activeTypes = classTypes.filter(ct => ct.is_active && ct.sku);
      
      const pricePromises = activeTypes.map(ct => 
        axios.get(`${API}/woo-product-by-sku?sku=${encodeURIComponent(ct.sku)}`)
          .then(res => ({ id: ct.id, sku: ct.sku, wooPrice: res.data?.price ? parseFloat(res.data.price) : null }))
          .catch(() => ({ id: ct.id, sku: ct.sku, wooPrice: null }))
      );
      
      const results = await Promise.all(pricePromises);
      
      // Create a map of classTypeId -> wooPrice
      const priceMap = {};
      results.forEach(r => {
        if (r.wooPrice !== null && r.wooPrice > 0) {
          priceMap[r.id] = r.wooPrice;
        }
      });
      
      console.log('Queue class type WooCommerce prices:', priceMap);
      setClassTypesWithPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch class type prices:', error);
    } finally {
      setLoadingPrices(false);
    }
  };
  
  // Helper to get WooCommerce price for a class type
  const getWooPrice = (classTypeId) => {
    return classTypesWithPrices[classTypeId] || 0;
  };
  
  const fetchPaymentConfig = async () => {
    try {
      console.log('Fetching Stripe config...');
      const response = await axios.get(`${API}/stripe/config`);
      console.log('Stripe config fetched:', { mode: response.data.mode, hasPublishableKey: !!response.data.publishable_key });
      setPaymentConfig(response.data);
      if (response.data?.publishable_key) {
        const stripe = loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    }
  };
  
  const fetchFeeRates = async () => {
    try {
      const [rushResponse, afterHoursResponse, mileageResponse] = await Promise.all([
        axios.get(`${API}/woo-product-by-sku?sku=RUSH`).catch(() => ({ data: null })),
        axios.get(`${API}/woo-product-by-sku?sku=AFTERHOURS`).catch(() => ({ data: null })),
        axios.get(`${API}/woo-product-by-sku?sku=MILES`).catch(() => ({ data: null }))
      ]);
      
      // Only use WooCommerce prices - no fallbacks
      setFeeRates({
        rushPerStudent: rushResponse.data?.price ? parseFloat(rushResponse.data.price) : 0,
        afterHoursPerStudent: afterHoursResponse.data?.price ? parseFloat(afterHoursResponse.data.price) : 0,
        mileagePerMile: mileageResponse.data?.price ? parseFloat(mileageResponse.data.price) : 0
      });
      
      // Log for debugging
      console.log('Fee rates from WooCommerce:', {
        rush: rushResponse.data?.price,
        afterHours: afterHoursResponse.data?.price,
        mileage: mileageResponse.data?.price
      });
    } catch (error) {
      console.error('Failed to fetch fee rates from WooCommerce:', error);
    }
  };

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pending-queue`);
      setQueueEntries(response.data || []);
    } catch (error) {
      console.error('Error fetching queue:', error);
      toast.error('Failed to fetch pending queue');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total for adding student - uses WooCommerce price
  const getAddStudentTotal = () => {
    const wooPrice = getWooPrice(newEntry.classTypeId);
    const basePrice = wooPrice > 0 ? wooPrice : 0;
    const afterHoursFee = manualFees.afterHours ? feeRates.afterHoursPerStudent : 0;
    const rushFee = manualFees.rushFee ? feeRates.rushPerStudent : 0;
    const mileageFee = manualFees.mileage * feeRates.mileagePerMile;
    return basePrice + afterHoursFee + rushFee + mileageFee;
  };

  const handleAddManual = async (payNow = false) => {
    if (!newEntry.firstName || !newEntry.lastName || !newEntry.email || !newEntry.classTypeId) {
      toast.error('Please fill in required fields');
      return;
    }

    if (payNow) {
      // Open payment dialog
      const entryData = {
        student: {
          first_name: newEntry.firstName,
          last_name: newEntry.lastName,
          email: newEntry.email,
          phone: newEntry.phone
        },
        class_type_id: newEntry.classTypeId,
        class_type_name: classTypes.find(ct => ct.id === newEntry.classTypeId)?.name,
        class_address: newEntry.street ? {
          street: newEntry.street,
          city: newEntry.city,
          state: newEntry.state,
          zip: newEntry.zip
        } : null,
        instructor_notes: newEntry.instructorNotes || null,
        original_class_date: newEntry.originalClassDate || null,  // For reschedule tracking
        fees: {
          after_hours: manualFees.afterHours,
          rush: manualFees.rushFee,
          mileage: manualFees.mileage
        }
      };
      setPaymentEntry(entryData);
      setShowAddDialog(false);
      // Re-fetch payment config to get fresh client token
      await fetchPaymentConfig();
      setShowPaymentDialog(true);
      return;
    }

    // Add without payment
    try {
      await axios.post(`${API}/admin/pending-queue/manual`, {
        student: {
          first_name: newEntry.firstName,
          last_name: newEntry.lastName,
          email: newEntry.email,
          phone: newEntry.phone
        },
        class_type_id: newEntry.classTypeId,
        class_address: newEntry.street ? {
          street: newEntry.street,
          city: newEntry.city,
          state: newEntry.state,
          zip: newEntry.zip
        } : null,
        instructor_notes: newEntry.instructorNotes || null,
        original_class_date: newEntry.originalClassDate || null,  // For reschedule tracking
        fees: {
          after_hours: manualFees.afterHours,
          rush: manualFees.rushFee,
          mileage: manualFees.mileage
        },
        payment_status: 'unpaid'
      });
      
      toast.success('Student added to pending queue (unpaid)');
      setShowAddDialog(false);
      setNewEntry({
        firstName: '', lastName: '', email: '', phone: '',
        classTypeId: '', street: '', city: '', state: '', zip: '', instructorNotes: '', originalClassDate: ''
      });
      setManualFees({ afterHours: false, rushFee: false, mileage: 0 });
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to queue');
    }
  };

  const handleRemoveFromQueue = async (entryId, entry = null) => {
    if (!window.confirm('Remove this student from the queue?')) return;
    
    // Check if student has an eCard
    const student = entry?.student;
    const hasEcard = student?.ecard_ordered === true;
    
    // Handle eCard logic
    let returnEcard = false;
    if (hasEcard) {
      // Student has an eCard assigned - ask about returning it
      returnEcard = window.confirm(
        '🎴 Return eCard to Inventory?\n\n' +
        'This student has an eCard assigned.\n\n' +
        'Click OK to return the card to inventory.\n' +
        'Click Cancel to keep the inventory unchanged.'
      );
    } else {
      // No eCard assigned - just show info message
      toast.info('No card assigned yet - proceeding with deletion');
    }
    
    try {
      const response = await axios.delete(`${API}/admin/pending-queue/${entryId}?return_ecard=${returnEcard}`);
      let msg = 'Removed from queue';
      if (response.data.ecard_returned) {
        if (response.data.ecard_reassigned_to) {
          msg += ` • eCard reassigned to ${response.data.ecard_reassigned_to}`;
        } else {
          msg += ' • eCard returned to inventory';
        }
      }
      toast.success(msg);
      setSelectedEntryIds(prev => prev.filter(id => id !== entryId));
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  // Save notes for a queue entry
  const handleSaveNotes = async (entryId) => {
    try {
      await axios.patch(`${API}/admin/pending-queue/${entryId}/notes`, {
        notes: editingNotesValue.trim() || null
      });
      toast.success('Notes saved');
      setEditingNotesId(null);
      setEditingNotesValue('');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  // Start editing notes for an entry
  const startEditingNotes = (entry) => {
    setEditingNotesId(entry.id);
    setEditingNotesValue(entry.student?.notes || entry.notes || '');
  };

  // Cancel editing notes
  const cancelEditingNotes = () => {
    setEditingNotesId(null);
    setEditingNotesValue('');
  };

  // Get unique class types from queue entries for filter dropdown
  const uniqueClassTypes = [...new Set(queueEntries.map(e => e.class_type_id))].map(id => {
    const entry = queueEntries.find(e => e.class_type_id === id);
    return { id, name: entry?.class_type_name || id };
  });

  // Sort and filter queue entries
  const sortedAndFilteredEntries = [...queueEntries]
    .filter(entry => filterClassType === 'all' || entry.class_type_id === filterClassType)
    .sort((a, b) => {
      if (sortBy === 'classType') {
        return (a.class_type_name || '').localeCompare(b.class_type_name || '');
      }
      // Default: sort by date added (newest first)
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  // Multi-select helper functions
  const toggleEntrySelection = (entryId) => {
    setSelectedEntryIds(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const selectAllEntries = () => {
    // Only select filtered entries
    const filteredIds = sortedAndFilteredEntries.map(e => e.id);
    if (selectedEntryIds.length === filteredIds.length && filteredIds.every(id => selectedEntryIds.includes(id))) {
      setSelectedEntryIds([]);
    } else {
      setSelectedEntryIds(filteredIds);
    }
  };

  const getSelectedEntries = () => {
    return queueEntries.filter(e => selectedEntryIds.includes(e.id));
  };

  // Open dialogs for bulk actions
  const openBulkAssignDialog = () => {
    if (selectedEntryIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    setAssignForm({ bookingId: '', asAddon: true });
    setShowAssignDialog(true);
  };

  const openBulkCreateClassDialog = () => {
    if (selectedEntryIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    // Check all selected have same class type
    const selectedEntries = getSelectedEntries();
    const classTypes = [...new Set(selectedEntries.map(e => e.class_type_id))];
    if (classTypes.length > 1) {
      toast.error('All selected students must have the same class type');
      return;
    }
    setCreateClassForm({ date: '', time: '' });
    setShowCreateClassDialog(true);
  };

  const handleAssignToClass = async () => {
    // Handle both single entry (selectedEntry) and bulk selection (selectedEntryIds)
    const entriesToAssign = selectedEntry ? [selectedEntry] : getSelectedEntries();
    
    if (entriesToAssign.length === 0 || !assignForm.bookingId) {
      toast.error('Please select a class');
      return;
    }

    try {
      // Assign each entry to the class
      for (const entry of entriesToAssign) {
        await axios.post(`${API}/admin/pending-queue/assign-to-class`, {
          queue_entry_id: entry.id,
          booking_id: assignForm.bookingId,
          as_addon: assignForm.asAddon
        });
      }
      
      const count = entriesToAssign.length;
      toast.success(`${count} student${count > 1 ? 's' : ''} assigned to class! Email${count > 1 ? 's' : ''} sent.`);
      setShowAssignDialog(false);
      setSelectedEntry(null);
      setSelectedEntryIds([]);
      setAssignForm({ bookingId: '', asAddon: true });
      fetchQueue();
      onRefreshBookings?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign to class');
    }
  };

  const handleCreateClass = async () => {
    // Handle both single entry (selectedEntry) and bulk selection (selectedEntryIds)
    const entriesToUse = selectedEntry ? [selectedEntry] : getSelectedEntries();
    
    if (entriesToUse.length === 0 || !createClassForm.date || !createClassForm.time) {
      toast.error('Please select date and time');
      return;
    }

    // Calculate end time based on class duration (use first entry's class type)
    const classType = classTypes.find(ct => ct.id === entriesToUse[0].class_type_id);
    const durationHours = classType?.duration_hours || 4;
    
    const startDateTime = new Date(`${createClassForm.date}T${createClassForm.time}`);
    const endDateTime = new Date(startDateTime.getTime() + durationHours * 60 * 60 * 1000);
    
    try {
      await axios.post(`${API}/admin/pending-queue/create-class`, {
        queue_entry_ids: entriesToUse.map(e => e.id),
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString()
      });
      
      const count = entriesToUse.length;
      toast.success(`New class created with ${count} student${count > 1 ? 's' : ''}! Confirmation email${count > 1 ? 's' : ''} sent.`);
      setShowCreateClassDialog(false);
      setSelectedEntry(null);
      setSelectedEntryIds([]);
      setCreateClassForm({ date: '', time: '' });
      fetchQueue();
      onRefreshBookings?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create class');
    }
  };

  const handleMoveToQueue = async () => {
    if (!selectedBookingForMove) return;

    try {
      const payload = {
        booking_id: selectedBookingForMove.id,
        student_emails: moveToQueueForm.moveAll ? null : moveToQueueForm.studentEmails
      };

      await axios.post(`${API}/admin/pending-queue/from-booking`, payload);
      
      const count = moveToQueueForm.moveAll 
        ? selectedBookingForMove.students?.length || 0 
        : moveToQueueForm.studentEmails.length;
      
      toast.success(`Moved ${count} student(s) to pending queue`);
      setShowMoveToQueueDialog(false);
      setSelectedBookingForMove(null);
      setMoveToQueueForm({ studentEmails: [], moveAll: true });
      fetchQueue();
      onRefreshBookings?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to queue');
    }
  };
  
  // Calculate total for queue payment - uses WooCommerce price
  const getPaymentTotal = () => {
    if (!paymentEntry) return 0;
    // Check if paymentEntry has fees from add dialog or needs to use manualFees
    const fees = paymentEntry.fees || manualFees;
    const wooPrice = getWooPrice(paymentEntry.class_type_id);
    const basePrice = wooPrice > 0 ? wooPrice : 0;
    const afterHoursFee = fees.after_hours || fees.afterHours ? feeRates.afterHoursPerStudent : 0;
    const rushFee = fees.rush || fees.rushFee ? feeRates.rushPerStudent : 0;
    const mileageFee = (fees.mileage || 0) * feeRates.mileagePerMile;
    const subtotal = basePrice + afterHoursFee + rushFee + mileageFee;
    return Math.max(0, subtotal - getDiscountAmount());
  };
  
  // Create Stripe order for queue entry (new or existing) - using POS endpoint
  const createQueueStripeOrder = useCallback(async () => {
    try {
      const fees = paymentEntry?.fees || manualFees;
      const classTypeId = paymentEntry?.class_type_id || newEntry.classTypeId;
      const classType = classTypes.find(c => c.id === classTypeId);
      
      console.log('Creating queue order:', { classTypeId, classType, fees, paymentEntry });
      
      if (!classType) {
        throw new Error('Class type not found');
      }
      
      // Use WooCommerce price instead of local price
      const wooPrice = getWooPrice(classTypeId);
      const classPrice = wooPrice > 0 ? wooPrice : 0;
      
      console.log('Queue order using WooCommerce price:', { classTypeId, wooPrice, classPrice });
      
      if (classPrice <= 0) {
        throw new Error('Invalid class price - no WooCommerce price found for this class type');
      }
      
      // Build items array like POS
      const items = [{
        sku: classType.id,
        name: classType.name,
        quantity: 1,
        price: classPrice
      }];
      
      const requestData = {
        items: items,
        fees: {
          after_hours: fees?.after_hours || fees?.afterHours || false,
          rush: fees?.rush || fees?.rushFee || false,
          mileage: fees?.mileage || 0
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total_quantity: 1
      };
      
      console.log('Queue order request:', requestData);
      
      const response = await axios.post(`${API}/pos/create-order`, requestData);
      
      console.log('Queue order response:', response.data);
      
      return response.data.stripe_order_id;
    } catch (error) {
      console.error('Error creating Stripe order:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create payment order');
      throw error;
    }
  }, [paymentEntry, manualFees, classTypes, newEntry.classTypeId, discount, feeRates]);
  
  // Memoized Stripe hosted fields styles to prevent re-render issues
  const stripeFieldStyles = useMemo(() => ({
    'input': {
      'color': '#ffffff',
      'font-size': '14px',
      'background-color': '#334155',
      'border-radius': '6px',
      'padding': '12px'
    },
    ':focus': {
      'color': '#ffffff'
    },
    '.invalid': {
      'color': '#ef4444'
    }
  }), []);
  
  // Calculate discount amount based on type - uses WooCommerce price
  const getDiscountAmount = () => {
    const classTypeId = paymentEntry?.class_type_id || newEntry.classTypeId;
    const wooPrice = getWooPrice(classTypeId);
    const classPrice = wooPrice > 0 ? wooPrice : 0;
    const feesTotal = 
      (manualFees.afterHours ? feeRates.afterHoursPerStudent : 0) +
      (manualFees.rushFee ? feeRates.rushPerStudent : 0) +
      (manualFees.mileage * feeRates.mileagePerMile);
    const subtotal = classPrice + feesTotal;
    
    if (!discount.value || discount.value <= 0) return 0;
    
    if (discount.type === 'percent') {
      const percent = Math.min(discount.value, 100);
      return (subtotal * percent) / 100;
    } else {
      return Math.min(discount.value, subtotal);
    }
  };
  
  // Handle successful queue payment
  const handleQueuePaymentSuccess = async (data) => {
    setPaymentProcessing(true);
    try {
      // Capture the payment
      await axios.post(`${API}/payments/capture-order/${data.orderID}`);
      
      const fees = paymentEntry.fees || manualFees;
      const totalPaid = getPaymentTotal();
      
      // Add to queue with paid status
      await axios.post(`${API}/admin/pending-queue/manual`, {
        student: paymentEntry.student,
        class_type_id: paymentEntry.class_type_id,
        class_address: paymentEntry.class_address,
        instructor_notes: paymentEntry.instructor_notes,
        fees: {
          after_hours: fees.after_hours || fees.afterHours || false,
          rush: fees.rush || fees.rushFee || false,
          mileage: fees.mileage || 0
        },
        payment_status: 'paid',
        amount_paid: totalPaid,
        stripe_order_id: data.orderID
      });
      
      toast.success('Payment successful! Student added to queue.');
      setShowPaymentDialog(false);
      setPaymentEntry(null);
      setNewEntry({
        firstName: '', lastName: '', email: '', phone: '',
        classTypeId: '', street: '', city: '', state: '', zip: '', instructorNotes: ''
      });
      setManualFees({ afterHours: false, rushFee: false, mileage: 0 });
      setDiscount({ type: 'dollar', value: 0, reason: '' });
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleGeneratePaymentLink = async (entryId) => {
    try {
      const response = await axios.post(`${API}/admin/pending-queue/${entryId}/generate-payment-link`);
      toast.success('Payment link generated');
      if (response.data.payment_link) {
        window.open(response.data.payment_link, '_blank');
      }
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payment link');
    }
  };

  const handleMarkPaid = async (entryId) => {
    try {
      await axios.post(`${API}/admin/pending-queue/${entryId}/mark-paid?amount=0`);
      toast.success('Marked as paid');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  // Mark/unmark entry for reschedule tracking
  const handleToggleReschedule = async (entry) => {
    try {
      if (entry.reschedule_status === 'pending_reschedule') {
        // Unmark
        await axios.post(`${API}/admin/reschedule/unmark`, { queue_entry_id: entry.id });
        toast.success('Reschedule tag removed');
      } else {
        // Mark for reschedule
        await axios.post(`${API}/admin/reschedule/mark`, { 
          queue_entry_id: entry.id,
          original_class_date: entry.original_class_date || new Date().toISOString()
        });
        toast.success('Student marked for reschedule - 60-day countdown started');
      }
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reschedule status');
    }
  };

  const openMoveToQueueDialog = (booking) => {
    setSelectedBookingForMove(booking);
    setMoveToQueueForm({ studentEmails: [], moveAll: true });
    setShowMoveToQueueDialog(true);
  };

  // Get available bookings for assignment (future classes that can accept students)
  const availableBookings = bookings.filter(b => 
    b.status !== 'rejected' &&
    b.status !== 'rejected_no_refund' &&
    b.status !== 'moved_to_queue' &&
    new Date(b.start_time) > new Date()
  );

  // Bookings that can have students moved to queue
  // By default: only classes within last 10 days or future classes
  // With showOlderBookings: include all past classes
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
  
  const bookingsWithStudents = bookings.filter(b => {
    // Basic filters
    if (!b.students?.length) return false;
    if (b.status === 'rejected' || b.status === 'moved_to_queue') return false;
    
    const bookingDate = new Date(b.start_time);
    const now = new Date();
    
    // Future classes always included
    if (bookingDate > now) return true;
    
    // Past classes: check if within 10 days or showOlderBookings is enabled
    if (showOlderBookings) return true;
    return bookingDate >= tenDaysAgo;
  });
  
  // Count of older bookings (more than 10 days ago) for the button label
  const olderBookingsCount = bookings.filter(b => {
    if (!b.students?.length) return false;
    if (b.status === 'rejected' || b.status === 'moved_to_queue') return false;
    const bookingDate = new Date(b.start_time);
    return bookingDate < tenDaysAgo;
  }).length;

  return (
    <>
      {/* Sidebar Toggle Button */}
      <div 
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 transition-transform duration-300 ${isOpen ? 'translate-x-full' : ''}`}
      >
        <Button
          onClick={onToggle}
          className="rounded-l-lg rounded-r-none bg-amber-600 hover:bg-amber-500 text-white px-5 py-20 shadow-lg"
          data-testid="pending-queue-toggle"
        >
          <div className="flex flex-col items-center gap-2">
            <Users className="h-6 w-6" />
            <span className="text-sm font-medium writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>
              Pending Queue ({queueEntries.length})
            </span>
          </div>
        </Button>
      </div>

      {/* Sidebar Panel */}
      <div 
        className={`fixed right-0 top-0 h-full w-96 bg-slate-800 border-l border-slate-700 shadow-2xl z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="pending-queue-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-slate-700 bg-slate-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-600 flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-100">Pending Queue</h2>
                  <p className="text-xs text-white">{queueEntries.length} student(s) waiting</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="text-white hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="p-3 border-b border-slate-700 space-y-2">
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                data-testid="add-to-queue-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Student
              </Button>
              <Select onValueChange={(bookingId) => {
                if (bookingId === '__show_older__') {
                  setShowOlderBookings(!showOlderBookings);
                  return;
                }
                const booking = bookingsWithStudents.find(b => b.id === bookingId);
                if (booking) openMoveToQueueDialog(booking);
              }}>
                <SelectTrigger className="flex-1 bg-slate-700 border-teal-600 text-slate-200" data-testid="move-from-booking-select">
                  <ArrowLeftRight className="h-4 w-4 mr-1" />
                  <span className="text-xs">From Booking</span>
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-72">
                  {bookingsWithStudents.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-white text-center">
                      No recent bookings with students
                    </div>
                  ) : (
                    bookingsWithStudents.map(booking => (
                      <SelectItem key={booking.id} value={booking.id} className="text-slate-200">
                        <div className="flex flex-col">
                          <span className="text-xs">{booking.class_type_name}</span>
                          <span className="text-xs text-white">
                            {format(new Date(booking.start_time), 'MMM d')} - {booking.students?.length} student(s)
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                  {/* Toggle for older bookings */}
                  {olderBookingsCount > 0 && (
                    <>
                      <div className="border-t border-teal-600 my-1" />
                      <SelectItem 
                        value="__show_older__" 
                        className={`text-xs ${showOlderBookings ? 'text-amber-400' : 'text-white'}`}
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          {showOlderBookings 
                            ? 'Hide older classes' 
                            : `Show older classes (${olderBookingsCount})`
                          }
                        </div>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDistanceCalc(true)}
              className="w-full border-cyan-700 text-cyan-400 hover:bg-cyan-900/30 h-7 text-xs"
              data-testid="queue-distance-calc-btn"
            >
              <Navigation className="h-3 w-3 mr-1" />
              Distance Calculator
            </Button>
          </div>

          {/* Queue List */}
          <ScrollArea className="flex-1 p-3">
            {loading ? (
              <div className="text-center text-white py-8">Loading...</div>
            ) : queueEntries.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No students in queue</p>
                <p className="text-xs mt-1">Add students manually or move from existing bookings</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sort & Filter Controls */}
                <div className="flex gap-2 p-2 bg-slate-800 rounded-lg border border-teal-600">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="flex-1 h-8 bg-slate-700 border-teal-600 text-slate-200 text-xs">
                      <ArrowUpDown className="h-3 w-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="date" className="text-slate-200 text-xs">Sort by Date Added</SelectItem>
                      <SelectItem value="classType" className="text-slate-200 text-xs">Sort by Class Type</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterClassType} onValueChange={setFilterClassType}>
                    <SelectTrigger className="flex-1 h-8 bg-slate-700 border-teal-600 text-slate-200 text-xs">
                      <Filter className="h-3 w-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all" className="text-slate-200 text-xs">All Class Types</SelectItem>
                      {uniqueClassTypes.map(ct => (
                        <SelectItem key={ct.id} value={ct.id} className="text-slate-200 text-xs">
                          {ct.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bulk Actions Bar */}
                <div className="flex items-center justify-between p-2 bg-slate-800 rounded-lg border border-teal-600">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedEntryIds.length === sortedAndFilteredEntries.length && sortedAndFilteredEntries.length > 0}
                      onCheckedChange={selectAllEntries}
                      className="border-slate-500 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                      data-testid="select-all-queue"
                    />
                    <span className="text-xs text-white">
                      {selectedEntryIds.length > 0 ? `${selectedEntryIds.length} selected` : `Select all (${sortedAndFilteredEntries.length})`}
                    </span>
                  </div>
                  {selectedEntryIds.length > 0 && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openBulkAssignDialog}
                        className="text-xs border-teal-600 text-teal-400 hover:bg-teal-900/30 h-7 px-2"
                        data-testid="bulk-assign-to-class"
                      >
                        <ArrowRight className="h-3 w-3 mr-1" />
                        To Class
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openBulkCreateClassDialog}
                        className="text-xs border-purple-600 text-purple-400 hover:bg-purple-900/30 h-7 px-2"
                        data-testid="bulk-create-class"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        New Class
                      </Button>
                    </div>
                  )}
                </div>

                {sortedAndFilteredEntries.length === 0 ? (
                  <div className="text-center text-slate-500 py-4">
                    <p className="text-sm">No students match filter</p>
                  </div>
                ) : sortedAndFilteredEntries.map(entry => (
                  <Card 
                    key={entry.id} 
                    className={`bg-slate-700/50 border-teal-600 p-3 ${selectedEntryIds.includes(entry.id) ? 'ring-2 ring-teal-500 border-teal-500' : ''}`}
                    data-testid={`queue-entry-${entry.id}`}
                  >
                    {/* Student Info with Checkbox */}
                    <div className="flex items-start gap-2 mb-2">
                      <Checkbox
                        checked={selectedEntryIds.includes(entry.id)}
                        onCheckedChange={() => toggleEntrySelection(entry.id)}
                        className="mt-1 border-slate-500 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                        data-testid={`select-queue-entry-${entry.id}`}
                      />
                      <div className="flex-1 flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-100">
                            {entry.student?.first_name} {entry.student?.last_name}
                          </p>
                          <p className="text-xs text-white">{entry.student?.email}</p>
                          <p className="text-xs text-slate-500">{entry.student?.phone}</p>
                        </div>
                        <Badge 
                          className={entry.payment_status === 'paid' 
                            ? 'bg-emerald-900 text-emerald-300' 
                            : 'bg-amber-900 text-amber-300'}
                        >
                          {entry.payment_status === 'paid' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                          ) : (
                            <><AlertCircle className="h-3 w-3 mr-1" /> Unpaid</>
                          )}
                        </Badge>
                      </div>
                    </div>

                    {/* Class Type */}
                    <div className="mb-2 p-2 bg-slate-800 rounded text-xs">
                      <span className="text-white">Wants:</span>
                      <span className="text-teal-400 ml-1 font-medium">{entry.class_type_name}</span>
                    </div>

                    {/* Original Booking Reference */}
                    {entry.original_booking_id && (
                      <div className="mb-2 p-2 bg-blue-900/30 border border-blue-700 rounded text-xs">
                        <span className="text-blue-300">From booking:</span>
                        <span className="text-blue-200 ml-1">
                          {entry.original_class_type} on {entry.original_class_date && format(new Date(entry.original_class_date), 'MMM d, yyyy')}
                        </span>
                      </div>
                    )}

                    {/* Student Notes Section */}
                    <div className="mb-2">
                      {editingNotesId === entry.id ? (
                        <div className="p-2 bg-slate-800 rounded border border-amber-600">
                          <label className="text-xs text-amber-400 font-medium block mb-1">
                            📝 Instructor Notes
                          </label>
                          <Textarea
                            value={editingNotesValue}
                            onChange={(e) => setEditingNotesValue(e.target.value)}
                            placeholder="Entry instructions, special needs, etc."
                            className="bg-slate-700 border-teal-600 text-slate-100 text-xs min-h-[60px] mb-2"
                            data-testid={`edit-notes-${entry.id}`}
                          />
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleSaveNotes(entry.id)}
                              className="flex-1 text-xs bg-amber-600 hover:bg-amber-500 text-white h-7"
                            >
                              Save Notes
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingNotes}
                              className="text-xs border-teal-600 text-white hover:bg-slate-700 h-7"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="p-2 bg-slate-800 rounded text-xs cursor-pointer hover:bg-slate-700 transition-colors border border-transparent hover:border-amber-600/50"
                          onClick={() => startEditingNotes(entry)}
                          data-testid={`notes-display-${entry.id}`}
                        >
                          <span className="text-amber-400 font-medium">📝 Notes: </span>
                          {(entry.student?.notes || entry.notes) ? (
                            <span className="text-white">{entry.student?.notes || entry.notes}</span>
                          ) : (
                            <span className="text-slate-500 italic">Click to add notes...</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedEntry(entry);
                          setAssignForm({ bookingId: '', asAddon: true });
                          setShowAssignDialog(true);
                        }}
                        className="flex-1 text-xs border-teal-600 text-teal-400 hover:bg-teal-900/30"
                        data-testid={`assign-to-class-${entry.id}`}
                      >
                        <ArrowRight className="h-3 w-3 mr-1" />
                        To Class
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedEntry(entry);
                          setCreateClassForm({ date: '', time: '' });
                          setShowCreateClassDialog(true);
                        }}
                        className="flex-1 text-xs border-purple-600 text-purple-400 hover:bg-purple-900/30"
                        data-testid={`create-class-${entry.id}`}
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        New Class
                      </Button>
                    </div>

                    {/* Payment Status Actions */}
                    {entry.payment_status !== 'paid' && (
                      <div className="flex gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMarkPaid(entry.id)}
                          className="flex-1 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          Mark Paid
                        </Button>
                        {entry.payment_link ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(entry.payment_link, '_blank')}
                            className="flex-1 text-xs text-white hover:text-white"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Payment Link
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleGeneratePaymentLink(entry.id)}
                            className="flex-1 text-xs text-white hover:text-white"
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Get Link
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Reschedule Tag Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleReschedule(entry)}
                      className={`w-full mt-1 text-xs ${
                        entry.reschedule_status === 'pending_reschedule' 
                          ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 border border-amber-600' 
                          : 'text-slate-400 hover:text-amber-400 hover:bg-slate-700'
                      }`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      {entry.reschedule_status === 'pending_reschedule' ? 'Reschedule Tagged ✓' : 'Mark for Reschedule'}
                    </Button>

                    {/* Remove Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveFromQueue(entry.id, entry)}
                      className="w-full mt-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove from Queue
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Add Student Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-amber-400" />
              Add Student to Queue
            </DialogTitle>
            <DialogDescription className="text-white">
              Manually add a student to the pending queue
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">First Name *</Label>
                <Input
                  value={newEntry.firstName}
                  onChange={(e) => setNewEntry({...newEntry, firstName: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="queue-first-name"
                />
              </div>
              <div>
                <Label className="text-white">Last Name *</Label>
                <Input
                  value={newEntry.lastName}
                  onChange={(e) => setNewEntry({...newEntry, lastName: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="queue-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Email *</Label>
              <Input
                type="email"
                value={newEntry.email}
                onChange={(e) => setNewEntry({...newEntry, email: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="queue-email"
              />
            </div>
            
            <div>
              <Label className="text-white">Phone</Label>
              <Input
                value={newEntry.phone}
                onChange={(e) => setNewEntry({...newEntry, phone: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="queue-phone"
              />
            </div>
            
            <div>
              <Label className="text-white">Class Type *</Label>
              <Select 
                value={newEntry.classTypeId} 
                onValueChange={(v) => setNewEntry({...newEntry, classTypeId: v})}
              >
                <SelectTrigger className="bg-slate-700 border-teal-600 text-slate-200" data-testid="queue-class-type">
                  <SelectValue placeholder="Select class type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {classTypes.filter(ct => ct.is_active).map(ct => (
                    <SelectItem key={ct.id} value={ct.id} className="text-slate-200">
                      {ct.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white">Street Address</Label>
              <Input
                value={newEntry.street}
                onChange={(e) => setNewEntry({...newEntry, street: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-white text-xs">City</Label>
                <Input
                  value={newEntry.city}
                  onChange={(e) => setNewEntry({...newEntry, city: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-white text-xs">State</Label>
                <Input
                  value={newEntry.state}
                  onChange={(e) => setNewEntry({...newEntry, state: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-white text-xs">ZIP</Label>
                <Input
                  value={newEntry.zip}
                  onChange={(e) => setNewEntry({...newEntry, zip: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">Instructor Notes</Label>
              <Textarea
                value={newEntry.instructorNotes}
                onChange={(e) => setNewEntry({...newEntry, instructorNotes: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                placeholder="Optional notes for instructor"
                rows={2}
              />
            </div>

            {/* Original Class Date - for reschedule tracking */}
            <div>
              <Label className="text-white">Original Class Date (Optional)</Label>
              <p className="text-xs text-slate-400 mb-1">For reschedule tracking - leave blank to use today's date</p>
              <Input
                type="date"
                value={newEntry.originalClassDate}
                onChange={(e) => setNewEntry({...newEntry, originalClassDate: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
              />
            </div>
            
            {/* Fee Toggles */}
            <div className="space-y-3 pt-3 border-t border-slate-700">
              <p className="text-xs text-white uppercase tracking-wide">Additional Fees</p>
              
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setManualFees(f => ({ ...f, afterHours: !f.afterHours }))}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                    manualFees.afterHours 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-slate-700 text-white hover:bg-slate-600'
                  }`}
                >
                  <Clock className="h-3.5 w-3.5" />
                  After Hours
                  {manualFees.afterHours && <CheckCircle className="h-3.5 w-3.5" />}
                </button>
                
                <button
                  type="button"
                  onClick={() => setManualFees(f => ({ ...f, rushFee: !f.rushFee }))}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                    manualFees.rushFee 
                      ? 'bg-red-600 text-white' 
                      : 'bg-slate-700 text-white hover:bg-slate-600'
                  }`}
                >
                  <AlertCircle className="h-3.5 w-3.5" />
                  Last Minute
                  {manualFees.rushFee && <CheckCircle className="h-3.5 w-3.5" />}
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-teal-400" />
                <span className="text-sm text-white">Miles:</span>
                <Input
                  type="number"
                  min="0"
                  value={manualFees.mileage}
                  onChange={(e) => setManualFees(f => ({ ...f, mileage: e.target.value === '' ? '' : e.target.value }))}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val < 0) setManualFees(f => ({ ...f, mileage: 0 }));
                  }}
                  className="w-20 h-8 bg-slate-700 border-teal-600 text-white"
                  placeholder="0"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDistanceCalc(true)}
                  className="h-8 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30"
                  data-testid="queue-add-distance-calc-btn"
                >
                  <Navigation className="h-4 w-4" />
                </Button>
                {manualFees.mileage > 0 && (
                  <span className="text-teal-400 text-sm">
                    +${(feeRates.mileagePerMile * manualFees.mileage).toFixed(2)}
                  </span>
                )}
              </div>
              
              {/* Price Summary */}
              {newEntry.classTypeId && (
                <div className="bg-slate-800/50 rounded p-3 mt-2">
                  {loadingPrices ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-teal-400 mr-2" />
                      <span className="text-sm text-white">Loading price from WooCommerce...</span>
                    </div>
                  ) : getWooPrice(newEntry.classTypeId) > 0 ? (
                    <>
                      <div className="flex justify-between text-sm text-white">
                        <span>Course Fee:</span>
                        <span>${getWooPrice(newEntry.classTypeId).toFixed(2)}</span>
                      </div>
                      {manualFees.afterHours && (
                        <div className="flex justify-between text-sm text-amber-400">
                          <span>After Hours:</span>
                          <span>+${feeRates.afterHoursPerStudent.toFixed(2)}</span>
                        </div>
                      )}
                      {manualFees.rushFee && (
                        <div className="flex justify-between text-sm text-red-400">
                          <span>Last Minute:</span>
                          <span>+${feeRates.rushPerStudent.toFixed(2)}</span>
                        </div>
                      )}
                      {manualFees.mileage > 0 && (
                        <div className="flex justify-between text-sm text-teal-400">
                          <span>Mileage ({manualFees.mileage} mi):</span>
                          <span>+${(feeRates.mileagePerMile * manualFees.mileage).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-white border-t border-teal-600 mt-2 pt-2">
                        <span>Total:</span>
                        <span className="text-emerald-400">${getAddStudentTotal().toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-amber-400 text-sm text-center py-2">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      No price found in WooCommerce for this class type
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowAddDialog(false)} className="text-white">
              Cancel
            </Button>
            <Button 
              onClick={() => handleAddManual(false)} 
              variant="outline"
              className="border-amber-600 text-amber-400 hover:bg-amber-900/30" 
              data-testid="add-without-paying"
            >
              Add Without Paying
            </Button>
            <Button 
              onClick={() => handleAddManual(true)} 
              className="bg-emerald-600 hover:bg-emerald-500" 
              data-testid="confirm-add-to-queue"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Pay Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Class Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => { setShowAssignDialog(open); if (!open) setSelectedEntry(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-teal-400" />
              Assign to Existing Class
            </DialogTitle>
            <DialogDescription className="text-white">
              {selectedEntry ? (
                <>Add <strong>{selectedEntry.student?.first_name} {selectedEntry.student?.last_name}</strong> to a class</>
              ) : selectedEntryIds.length > 0 ? (
                <>Add <strong>{selectedEntryIds.length} students</strong> to a class</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          
          {/* Show selected students for bulk */}
          {!selectedEntry && selectedEntryIds.length > 0 && (
            <div className="p-3 bg-slate-700 rounded border border-teal-600 max-h-32 overflow-y-auto">
              <p className="text-xs text-white mb-2">Selected students:</p>
              {getSelectedEntries().map(entry => (
                <p key={entry.id} className="text-sm text-slate-200">
                  • {entry.student?.first_name} {entry.student?.last_name}
                </p>
              ))}
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {availableBookings.length === 0 ? (
              <div className="p-4 bg-amber-900/30 border border-amber-700 rounded text-center">
                <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 font-medium">No available classes</p>
                <p className="text-sm text-amber-400 mt-1">
                  There are no future classes to assign this student to. Use "New Class" to create one.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-white">Select Class</Label>
                  <Select value={assignForm.bookingId} onValueChange={(v) => setAssignForm({...assignForm, bookingId: v})}>
                    <SelectTrigger className="bg-slate-700 border-teal-600 text-slate-200" data-testid="assign-booking-select">
                      <SelectValue placeholder="Choose a class" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      {availableBookings.map(booking => (
                        <SelectItem key={booking.id} value={booking.id} className="text-slate-200">
                          <div className="flex flex-col">
                            <span>{booking.class_type_name}</span>
                            <span className="text-xs text-white">
                              {format(new Date(booking.start_time), 'MMM d, yyyy h:mm a')} - {booking.students?.length} student(s)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 bg-blue-900/30 border border-blue-700 rounded text-sm text-blue-300">
                  <strong>Note:</strong> Appropriate confirmation emails will be sent to the student once assigned.
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAssignDialog(false)} className="text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleAssignToClass} 
              className="bg-teal-600 hover:bg-teal-500"
              disabled={!assignForm.bookingId}
              data-testid="confirm-assign-to-class"
            >
              Assign to Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Class Dialog */}
      <Dialog open={showCreateClassDialog} onOpenChange={(open) => { setShowCreateClassDialog(open); if (!open) setSelectedEntry(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-400" />
              Create New Class
            </DialogTitle>
            <DialogDescription className="text-white">
              {selectedEntry ? (
                <>Schedule a new class for <strong>{selectedEntry.student?.first_name} {selectedEntry.student?.last_name}</strong></>
              ) : selectedEntryIds.length > 0 ? (
                <>Schedule a new class for <strong>{selectedEntryIds.length} students</strong></>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Show class info and selected students */}
            {(selectedEntry || selectedEntryIds.length > 0) && (
              <div className="p-3 bg-slate-700/50 rounded space-y-2">
                <p className="text-sm text-white">
                  <span className="text-white">Class:</span> {(selectedEntry || getSelectedEntries()[0])?.class_type_name}
                </p>
                <p className="text-sm text-white">
                  <span className="text-white">Duration:</span> {classTypes.find(ct => ct.id === (selectedEntry || getSelectedEntries()[0])?.class_type_id)?.duration_hours || 4} hours
                </p>
                {!selectedEntry && selectedEntryIds.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-teal-600">
                    <p className="text-xs text-white mb-1">Students:</p>
                    {getSelectedEntries().map(entry => (
                      <p key={entry.id} className="text-sm text-slate-200">
                        • {entry.student?.first_name} {entry.student?.last_name}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-white">Date</Label>
              <Input
                type="date"
                value={createClassForm.date}
                onChange={(e) => setCreateClassForm({...createClassForm, date: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                min={format(new Date(), 'yyyy-MM-dd')}
                data-testid="create-class-date"
              />
            </div>

            <div>
              <Label className="text-white">Start Time</Label>
              <Input
                type="time"
                value={createClassForm.time}
                onChange={(e) => setCreateClassForm({...createClassForm, time: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="create-class-time"
              />
            </div>

            <div className="p-3 bg-purple-900/30 border border-purple-700 rounded text-sm text-purple-300">
              <strong>Note:</strong> A new booking will be created and confirmation email{(selectedEntryIds.length > 1 || (selectedEntry === null && selectedEntryIds.length > 1)) ? 's' : ''} sent to the student{(selectedEntryIds.length > 1 || (selectedEntry === null && selectedEntryIds.length > 1)) ? 's' : ''}.
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateClassDialog(false)} className="text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateClass} 
              className="bg-purple-600 hover:bg-purple-500"
              disabled={!createClassForm.date || !createClassForm.time}
              data-testid="confirm-create-class"
            >
              Create Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Queue Dialog */}
      <Dialog open={showMoveToQueueDialog} onOpenChange={setShowMoveToQueueDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-amber-400" />
              Move Students to Queue
            </DialogTitle>
            <DialogDescription className="text-white">
              {selectedBookingForMove && (
                <>From: <strong>{selectedBookingForMove.class_type_name}</strong> on {format(new Date(selectedBookingForMove.start_time), 'MMM d, yyyy')}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="moveAll"
                checked={moveToQueueForm.moveAll}
                onChange={(e) => setMoveToQueueForm({...moveToQueueForm, moveAll: e.target.checked, studentEmails: []})}
                className="rounded border-teal-600"
              />
              <Label htmlFor="moveAll" className="text-white">Move all students</Label>
            </div>

            {!moveToQueueForm.moveAll && selectedBookingForMove?.students && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                <p className="text-xs text-white">Select students to move:</p>
                {selectedBookingForMove.students.map(student => (
                  <label 
                    key={student.email} 
                    className="flex items-center gap-2 p-2 bg-slate-700/50 rounded cursor-pointer hover:bg-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={moveToQueueForm.studentEmails.includes(student.email)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMoveToQueueForm({
                            ...moveToQueueForm,
                            studentEmails: [...moveToQueueForm.studentEmails, student.email]
                          });
                        } else {
                          setMoveToQueueForm({
                            ...moveToQueueForm,
                            studentEmails: moveToQueueForm.studentEmails.filter(e => e !== student.email)
                          });
                        }
                      }}
                      className="rounded border-teal-600"
                    />
                    <span className="text-slate-200">{student.first_name} {student.last_name}</span>
                    <span className="text-xs text-white">({student.email})</span>
                  </label>
                ))}
              </div>
            )}

            <div className="p-3 bg-amber-900/30 border border-amber-700 rounded text-sm text-amber-300">
              <strong>Note:</strong> Students moved to queue will NOT receive any emails. They will be removed from the calendar event.
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMoveToQueueDialog(false)} className="text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleMoveToQueue} 
              className="bg-amber-600 hover:bg-amber-500"
              disabled={!moveToQueueForm.moveAll && moveToQueueForm.studentEmails.length === 0}
              data-testid="confirm-move-to-queue"
            >
              Move to Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Queue Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => { 
        if (!open) {
          setShowPaymentDialog(false);
          setPaymentEntry(null);
          setTargetBookingId('');
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-400" />
              Process Payment
            </DialogTitle>
          </DialogHeader>
          
          {paymentEntry && (
            <>
              {/* Student & Class Info */}
              <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                <p className="text-white text-sm mb-1">
                  <strong>Student:</strong> {paymentEntry.student?.first_name} {paymentEntry.student?.last_name}
                </p>
                <p className="text-white text-sm mb-2">
                  <strong>Class:</strong> {paymentEntry.class_type_name}
                </p>
                
                {/* Line Items */}
                <div className="space-y-2 border-t border-teal-600 pt-3">
                  <div className="flex justify-between text-white text-sm">
                    <span>Course Fee</span>
                    <span>${classTypes.find(ct => ct.id === paymentEntry.class_type_id)?.price || 75}</span>
                  </div>
                  
                  {/* Manual Fee Toggles */}
                  <div className="space-y-2 pt-2">
                    <p className="text-xs text-white uppercase tracking-wide">Additional Fees</p>
                    
                    {/* After Hours Fee */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setManualFees(f => ({ ...f, afterHours: !f.afterHours }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                          manualFees.afterHours 
                            ? 'bg-amber-600 text-white' 
                            : 'bg-slate-600 text-white hover:bg-slate-500'
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        After Hours
                        {manualFees.afterHours && <CheckCircle className="h-3.5 w-3.5" />}
                      </button>
                      {manualFees.afterHours && (
                        <span className="text-amber-400 text-sm">
                          +${feeRates.afterHoursPerStudent.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Rush/Last Minute Fee */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setManualFees(f => ({ ...f, rushFee: !f.rushFee }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                          manualFees.rushFee 
                            ? 'bg-red-600 text-white' 
                            : 'bg-slate-600 text-white hover:bg-slate-500'
                        }`}
                      >
                        <AlertCircle className="h-3.5 w-3.5" />
                        Last Minute
                        {manualFees.rushFee && <CheckCircle className="h-3.5 w-3.5" />}
                      </button>
                      {manualFees.rushFee && (
                        <span className="text-red-400 text-sm">
                          +${feeRates.rushPerStudent.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Mileage Fee */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-teal-400" />
                        <span className="text-sm text-white">Miles:</span>
                        <Input
                          type="number"
                          min="0"
                          value={manualFees.mileage}
                          onChange={(e) => setManualFees(f => ({ ...f, mileage: e.target.value === '' ? '' : e.target.value }))}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val) || val < 0) setManualFees(f => ({ ...f, mileage: 0 }));
                          }}
                          className="w-16 h-8 bg-slate-600 border-slate-500 text-white text-sm"
                          placeholder="0"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowDistanceCalc(true)}
                          className="h-8 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30"
                          data-testid="queue-pay-distance-calc-btn"
                        >
                          <Navigation className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {manualFees.mileage > 0 && (
                        <span className="text-teal-400 text-sm">
                          +${(feeRates.mileagePerMile * manualFees.mileage).toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Discount */}
                    <div className="border-t border-teal-600 pt-2 mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Percent className="h-3.5 w-3.5 text-purple-400" />
                          <span className="text-sm text-purple-400 font-medium">Discount</span>
                        </div>
                        <div className="flex bg-slate-600 rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => setDiscount({ ...discount, type: 'dollar' })}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              discount.type === 'dollar' 
                                ? 'bg-purple-600 text-white' 
                                : 'text-white hover:text-white'
                            }`}
                          >
                            $
                          </button>
                          <button
                            type="button"
                            onClick={() => setDiscount({ ...discount, type: 'percent' })}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              discount.type === 'percent' 
                                ? 'bg-purple-600 text-white' 
                                : 'text-white hover:text-white'
                            }`}
                          >
                            %
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="relative w-20">
                          {discount.type === 'dollar' && (
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs">$</span>
                          )}
                          <Input
                            type="number"
                            min="0"
                            max={discount.type === 'percent' ? 100 : undefined}
                            step={discount.type === 'percent' ? 1 : 0.01}
                            value={discount.value ?? ''}
                            onChange={(e) => setDiscount({ ...discount, value: e.target.value === '' ? '' : e.target.value })}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value);
                              if (isNaN(val) || val < 0) setDiscount(prev => ({ ...prev, value: 0 }));
                              else if (discount.type === 'percent' && val > 100) setDiscount(prev => ({ ...prev, value: 100 }));
                              else setDiscount(prev => ({ ...prev, value: val }));
                            }}
                            placeholder="0"
                            className={`${discount.type === 'dollar' ? 'pl-5' : 'pl-2'} pr-2 h-8 bg-slate-600 border-slate-500 text-white text-sm`}
                          />
                          {discount.type === 'percent' && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-xs">%</span>
                          )}
                        </div>
                        <Input
                          type="text"
                          value={discount.reason}
                          onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
                          placeholder="Reason"
                          className="flex-1 h-8 bg-slate-600 border-slate-500 text-white text-sm placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Discount Display */}
                {getDiscountAmount() > 0 && (
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-purple-400">
                      Discount {discount.type === 'percent' ? `(${discount.value}%)` : ''}
                      {discount.reason ? ` - ${discount.reason}` : ''}
                    </span>
                    <span className="text-purple-400">-${getDiscountAmount().toFixed(2)}</span>
                  </div>
                )}
                
                {/* Total */}
                <div className="border-t border-teal-600 mt-3 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total:</span>
                    <span className="text-teal-400 font-bold text-xl">
                      ${getPaymentTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Stripe Payment Form */}
              {stripePromise && (
                <Elements stripe={stripePromise}>
                  <div className="space-y-4">
                    {/* Card Number */}
                    <div>
                      <Label className="text-white text-sm mb-2 flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-slate-400" />
                        Card Number
                      </Label>
                      <div className={`bg-slate-700 border rounded-lg p-3 ${
                        queueCardFieldStatus.cardNumber.error 
                          ? 'border-red-500' 
                          : queueCardFieldStatus.cardNumber.complete 
                            ? 'border-green-500' 
                            : 'border-teal-600'
                      }`}>
                        <CardNumberElement 
                          options={ELEMENT_OPTIONS}
                          onChange={(e) => setQueueCardFieldStatus(prev => ({
                            ...prev,
                            cardNumber: { complete: e.complete, error: e.error?.message || null }
                          }))}
                        />
                      </div>
                    </div>
                    
                    {/* Expiry and CVC Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white text-sm mb-2 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          Expiry
                        </Label>
                        <div className={`bg-slate-700 border rounded-lg p-3 ${
                          queueCardFieldStatus.cardExpiry.error 
                            ? 'border-red-500' 
                            : queueCardFieldStatus.cardExpiry.complete 
                              ? 'border-green-500' 
                              : 'border-teal-600'
                        }`}>
                          <CardExpiryElement 
                            options={ELEMENT_OPTIONS}
                            onChange={(e) => setQueueCardFieldStatus(prev => ({
                              ...prev,
                              cardExpiry: { complete: e.complete, error: e.error?.message || null }
                            }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-white text-sm mb-2 flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-slate-400" />
                          CVC
                        </Label>
                        <div className={`bg-slate-700 border rounded-lg p-3 ${
                          queueCardFieldStatus.cardCvc.error 
                            ? 'border-red-500' 
                            : queueCardFieldStatus.cardCvc.complete 
                              ? 'border-green-500' 
                              : 'border-teal-600'
                        }`}>
                          <CardCvcElement 
                            options={ELEMENT_OPTIONS}
                            onChange={(e) => setQueueCardFieldStatus(prev => ({
                              ...prev,
                              cardCvc: { complete: e.complete, error: e.error?.message || null }
                            }))}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* ZIP Code */}
                    <div>
                      <Label className="text-white text-sm mb-2">ZIP Code</Label>
                      <Input
                        type="text"
                        placeholder="12345"
                        value={queuePostalCode}
                        onChange={(e) => setQueuePostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
                        className={`bg-slate-700 border text-white placeholder:text-slate-500 ${
                          queuePostalCode.length >= 5 ? 'border-green-500' : 'border-teal-600'
                        }`}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2 mt-4">
                      <Checkbox
                        id="queue-payment-terms"
                        checked={paymentTermsAccepted}
                        onCheckedChange={setPaymentTermsAccepted}
                        className="border-slate-500 data-[state=checked]:bg-teal-500"
                      />
                      <label htmlFor="queue-payment-terms" className="text-sm text-white cursor-pointer">
                        Customer accepts the Terms and Conditions
                      </label>
                    </div>
                    
                    <QueueStripeSubmitButton
                      onApprove={handleQueuePaymentSuccess}
                      onError={(err) => toast.error(err.message || 'Payment failed')}
                      disabled={paymentProcessing || !queueCardFieldStatus.cardNumber.complete || !queueCardFieldStatus.cardExpiry.complete || !queueCardFieldStatus.cardCvc.complete || queuePostalCode.length < 5}
                      termsAccepted={paymentTermsAccepted}
                      amount={getPaymentTotal()}
                      customerEmail={paymentEntry?.students?.[0]?.email || ''}
                      postalCode={queuePostalCode}
                      metadata={{
                        queue_entry_id: paymentEntry?.id || '',
                        class_type_id: paymentEntry?.class_type_id || ''
                      }}
                    />
                  </div>
                </Elements>
              )}
              
              {/* Show loading or error if Stripe not ready */}
              {!stripePromise && (
                <div className="text-center py-8">
                  {paymentConfig ? (
                    <>
                      <AlertCircle className="h-10 w-10 mx-auto mb-3 text-amber-400" />
                      <p className="text-amber-300 mb-2">Payment system not available</p>
                      <p className="text-sm text-white">Please check Stripe configuration.</p>
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-teal-400" />
                      <p className="text-white">Loading payment form...</p>
                    </>
                  )}
                </div>
              )}
              
              <p className="text-center text-xs text-slate-500 mt-2">
                🔒 Secure payment powered by Stripe
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Distance Calculator Dialog */}
      <DistanceCalculator
        open={showDistanceCalc}
        onClose={() => setShowDistanceCalc(false)}
        onImport={(miles) => setManualFees(f => ({ ...f, mileage: miles }))}
      />
    </>
  );
};

export default PendingQueueSidebar;
